import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { login as apiLogin } from '@/services/api';
import { trackSignUpConversion, trackTwitterSignUpConversion } from '@/utils/gtm';
import { initPostHog, captureEvent } from '@/utils/posthog';
import { sendLoginToExtension } from '@/lib/extensionUtils';
import { 
  trackLoginSuccess, 
  trackLoginFailed, 
  trackLogoutInitiated
} from '@/utils/eventTracking';
import { sessionTracker } from '@/utils/sessionTracker';
import { initializePaddle } from '@/services/paddle';

interface AuthContextType {
  token: string | null;
  login: (googleResponse: any) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Helper function to set domain-level cookies
const setDomainCookie = (name: string, value: string, days: number = 30) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  // Set cookie at domain level (not subdomain level)
  // Using .instavault.co will make it available across all subdomains
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=.instavault.co; SameSite=Lax`;
};

// Helper function to clear domain-level cookies
const clearDomainCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; domain=.instavault.co`;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('token'); // Initialize from localStorage
  });
  const navigate = useNavigate();

  // Initialize PostHog when the component mounts
  useEffect(() => {
    initPostHog();
  }, []);

  const login = async (googleResponse: any) => {
    const loginStartTime = Date.now();
    
    try {
      const data = await apiLogin(googleResponse.credential);

      if (data.is_new_user) {
        trackSignUpConversion();     
        // Track Twitter conversion only for new users
        trackTwitterSignUpConversion('tw-qh5c1-qi8x6', {
          email_address: data.email,
          phone_number: data.mobile
        });
      }

      if (!data.access_token) throw new Error('No token received');

      // Set in localStorage
      localStorage.setItem('user_id', data.user_id);
      localStorage.setItem('email', data.email);
      localStorage.setItem('token', data.access_token);
      
      // Set in domain-level cookies
      setDomainCookie('user_id', data.user_id);
      setDomainCookie('email', data.email);
      setDomainCookie('token', data.access_token);
      setDomainCookie('token_expiry', String(data.access_token_expiry));
      
      if (data.country_code) {
        localStorage.setItem('country_code', data.country_code);
      }
      if (data.mobile) {
        localStorage.setItem('mobile', data.mobile);
      }
      if (data.paddle_customer_id) {
        localStorage.setItem('paddle_customer_id', data.paddle_customer_id);
        // Re-initialize Paddle with customer ID
        initializePaddle(data.paddle_customer_id);
      } else {
        // Re-initialize Paddle with empty object if no customer ID
        initializePaddle();
      }
      localStorage.setItem('premiumUntil', data.premium_until);
      setToken(data.access_token); // Set the token in state
      updatePostHogIdentity(data.is_new_user);

      // Send login data to Instavault extension if available
      sendLoginToExtension({
        accessToken: data.access_token,
        userId: data.user_id,
        email: data.email,
        name:data.name,
        expiresAt: data.access_token_expiry // 24 hours from now
      });

      // Track successful login
      const timeToLogin = (Date.now() - loginStartTime) / 1000; // log in seconds
      trackLoginSuccess(data.premium_until, timeToLogin);

      if (data.is_new_user) {
        // Use the utility for capturing events
        captureEvent('Dashboard Sign up', {
          userId: data.user_id,
          email: data.email
        });
      }
    } catch (error) {
      console.error('Login failed:', error);
      trackLoginFailed('google', error instanceof Error ? error.message : 'Unknown error', document.referrer);
      throw error;
    }
  };

  // Add token to context value
  const value = {
    token,
    login,
    logout: () => {
      // Track logout initiation
      trackLogoutInitiated();
      
      // End dashboard session
      sessionTracker.endSession();
      
      // Clear from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('paddle_customer_id');
      
      // Clear from domain-level cookies
      clearDomainCookie('user_id');
      clearDomainCookie('email');
      clearDomainCookie('token');
      clearDomainCookie('token_expiry');
      
      setToken(null);
      navigate('/login');
    },
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
}

export const updatePostHogIdentity = (_is_new_user: boolean) => {
  try {
    if (typeof posthog !== 'undefined' && posthog) {
      // Identify the user with their unique ID and set their email
      const userId = localStorage.getItem('user_id') || undefined;
      const email = localStorage.getItem('email') || undefined;
      const anonymousId = getCookie('ph_anon_id');



      if (userId) {
        if(anonymousId){
          posthog.alias(userId,anonymousId);
          document.cookie = 'ph_anon_id=; ph_session_id=; domain=.instavault.co; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        }
        posthog.identify(userId, {email: email});
        

      } else {
        console.log("No user ID found, skipping PostHog identity update");
      }
    } else {
      console.log("PostHog not initialized, couldn't update identity");
    }
  } catch (error) {
    console.error("Error updating PostHog identity:", error);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};